import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import JobService from "../../services/JobService";
import { Job } from "../../lib/types/job";
import { Evaluation } from "../../lib/types/evaluation";
import moment from "moment";

function InterviewDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchJobDetail = async () => {
      if (!id) return;

      setIsLoading(true);
      setError("");

      try {
        const jobService = new JobService();
        const data = await jobService.getJob(id);
        setJob(data);
      } catch (err: any) {
        console.error(err);
        setError(
          err?.response?.data?.message ||
            t("interviewDetail.errors.loadingFailed")
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobDetail();
  }, [id, t]);

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8"
        style={{ paddingTop: "4rem" }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center py-12">
            <svg
              className="animate-spin h-12 w-12 text-indigo-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div
        className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8"
        style={{ paddingTop: "4rem" }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-red-500 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {error || t("interviewDetail.errors.notFound")}
            </h3>
            <button
              onClick={() => navigate("/my-page?tab=interviews")}
              className="mt-4 bg-indigo-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {t("interviewDetail.back")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: "4rem" }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate("/my-page?tab=interviews")}
          className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium mb-6 transition-colors"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t("interviewDetail.back")}
        </button>

        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 break-words">
                {t("interviewDetail.title")} #{job._id}
              </h1>
              <div className="flex items-center flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                <span className="flex items-center whitespace-nowrap">
                  <svg
                    className="w-4 h-4 mr-1 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {moment(job.createdAt).format("DD-MM-YYYY, h:mm a")}
                </span>
                <span className="flex items-center whitespace-nowrap">
                  <svg
                    className="w-4 h-4 mr-1 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                    />
                  </svg>
                  {job.language}
                </span>
                <span className="flex items-center whitespace-nowrap">
                  <svg
                    className="w-4 h-4 mr-1 shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {job.evaluations?.length || 0}{" "}
                  {t("interviewDetail.questions")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Questions and Answers */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {t("interviewDetail.questionsAnswers")}
          </h2>

          {job.evaluations && job.evaluations.length > 0 ? (
            <div className="space-y-6">
              {job.evaluations.map((evaluation: Evaluation, index: number) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  {/* Question */}
                  <div className="mb-4">
                    <div className="flex items-start mb-3">
                      <span className="shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-semibold mr-3">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {t("interviewDetail.question")}
                        </h3>
                        <p className="text-gray-700">{evaluation.question}</p>
                      </div>
                    </div>
                  </div>

                  {/* Answer */}
                  {evaluation.answer ? (
                    <div className="bg-blue-50 rounded-lg p-4 mb-4">
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">
                        {t("interviewDetail.yourAnswer")}
                      </h4>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {evaluation.answer}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4 text-center mb-4">
                      <p className="text-gray-500 text-sm">
                        {t("interviewDetail.noAnswer")}
                      </p>
                    </div>
                  )}

                  {/* Feedback */}
                  {evaluation.feedback && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-green-900 mb-2">
                        {t("interviewDetail.aiFeedback")}
                      </h4>
                      <p className="text-gray-700 whitespace-pre-wrap">
                        {evaluation.feedback}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              {t("interviewDetail.noQuestions")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InterviewDetail;
